import { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useConfig } from '../../context/ConfigContext';
import { useTheme } from '../../context/ThemeContext';

// Fallback colors if category doesn't have a color
const FALLBACK_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

export default function CategoryPieChart({ data, type, title, compact = false }) {
  const { formatAmount, config } = useConfig();
  const { isModernUI } = useTheme();
  const [hiddenCategories, setHiddenCategories] = useState(new Set());

  // Convert object format { category: { total, totalTDS, count, color } } to array format
  // For income with TDS, show net amount (total - TDS). Otherwise show gross.
  const dataArray = data && typeof data === 'object' && !Array.isArray(data)
    ? Object.entries(data).map(([category, values], index) => {
        const hasTDS = values.totalTDS && values.totalTDS > 0;
        return {
          category,
          total: type === 'income' && hasTDS ? values.total - values.totalTDS : values.total,
          grossTotal: values.total,
          totalTDS: values.totalTDS || 0,
          count: values.count,
          color: values.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length]
        };
      })
    : Array.isArray(data) ? data : [];

  const toggleCategory = (category) => {
    setHiddenCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  if (!dataArray || dataArray.length === 0) {
    return (
      <div className={`rounded-2xl ${compact ? 'p-3' : 'p-6'} bg-white border border-surface-200`}>
        <h3 className={`font-semibold text-surface-900 ${compact ? 'text-xs mb-2' : 'text-lg mb-4'}`}>{title}</h3>
        <div className={`flex items-center justify-center text-surface-400 ${compact ? 'h-24 text-xs' : 'h-64'}`}>
          No data
        </div>
      </div>
    );
  }

  // All chart data with colors assigned
  const allChartData = dataArray.map((item, index) => ({
    name: item.category,
    value: item.total,
    grossTotal: item.grossTotal,
    totalTDS: item.totalTDS,
    color: item.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
    isHidden: hiddenCategories.has(item.category)
  }));

  // Filtered data for the pie chart (only visible categories)
  const visibleChartData = allChartData.filter(item => !item.isHidden);

  // Total of visible items only
  const visibleTotal = visibleChartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = visibleTotal > 0 ? ((data.value / visibleTotal) * 100).toFixed(1) : 0;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-surface-200">
          <p className="font-medium text-surface-900">{data.name}</p>
          <p className="text-surface-600">{formatAmount(data.value)}</p>
          {type === 'income' && data.totalTDS > 0 && (
            <>
              <p className="text-surface-400 text-sm">TDS: {formatAmount(data.totalTDS)}</p>
              <p className="text-surface-500 text-sm">Gross: {formatAmount(data.grossTotal)}</p>
            </>
          )}
          <p className="text-surface-500 text-sm">{percentage}%</p>
        </div>
      );
    }
    return null;
  };

  const renderLegend = () => {
    // For compact mode, only show top 3 categories
    const legendData = compact ? allChartData.slice(0, 3) : allChartData;
    const hasMore = compact && allChartData.length > 3;

    return (
      <div className={`flex flex-wrap gap-1 justify-center ${compact ? 'mt-2' : 'mt-4'}`}>
        {legendData.map((item, index) => {
          const isHidden = hiddenCategories.has(item.name);
          const percentage = visibleTotal > 0 && !isHidden ? ((item.value / visibleTotal) * 100).toFixed(0) : 0;
          return (
            <button
              key={index}
              onClick={() => toggleCategory(item.name)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-all cursor-pointer hover:bg-surface-100 ${isHidden ? 'opacity-40' : ''}`}
            >
              <div
                className={`rounded-full flex-shrink-0 ${compact ? 'w-1.5 h-1.5' : 'w-2.5 h-2.5'}`}
                style={{
                  backgroundColor: isHidden ? '#9ca3af' : item.color
                }}
              />
              <span
                className={`truncate font-medium ${compact ? 'max-w-[40px] text-[8px]' : 'max-w-[70px] text-[11px]'} ${isHidden ? 'line-through text-surface-400' : 'text-surface-600'}`}
              >
                {item.name}
              </span>
              {!isHidden && !compact && (
                <span className="text-[10px] text-surface-400 font-medium">
                  {percentage}%
                </span>
              )}
            </button>
          );
        })}
        {hasMore && (
          <span className="text-[8px] text-surface-400 px-1">+{allChartData.length - 3}</span>
        )}
      </div>
    );
  };

  return (
    <div className={`rounded-2xl ${compact ? 'p-3' : 'p-6'} bg-white border border-surface-200`}>
      <div className={`flex items-center justify-between ${compact ? 'mb-1' : 'mb-2'}`}>
        <h3 className={`font-semibold text-surface-900 ${compact ? 'text-xs' : 'text-base'}`}>{title}</h3>
      </div>
      <div className={`[&_svg]:outline-none [&_svg]:border-none [&_*]:outline-none ${compact ? 'h-24' : 'h-48'}`}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart style={{ outline: 'none' }}>
            <Pie
              data={visibleChartData}
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius={compact ? 42 : 80}
              paddingAngle={1}
              dataKey="value"
              stroke="#fff"
              strokeWidth={compact ? 1 : 2}
              style={{ outline: 'none' }}
            >
              {visibleChartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  style={{ outline: 'none' }}
                />
              ))}
            </Pie>
            {!compact && <Tooltip content={<CustomTooltip />} />}
          </PieChart>
        </ResponsiveContainer>
      </div>
      {/* Total display below chart */}
      <div className={`text-center ${compact ? 'mt-1' : 'mt-2'}`}>
        <span
          className={`font-bold ${compact ? 'text-[10px]' : 'text-base'}`}
          style={{ color: type === 'income' ? config.incomeColor : config.expenseColor }}
        >
          {formatAmount(visibleTotal)}
        </span>
      </div>
      {renderLegend()}
    </div>
  );
}
